import React, { useState, useEffect, useCallback, ChangeEvent, FormEvent, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Application, ApplicationFormData, FeatureFormData, Plan, PlanFormData, PricingTier, ApplicationAnalytics, formatErrorMessage } from '../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSave, faEdit, faTrash, faImage, faMinus, faList, faChartBar, faBolt, faCopy, faCode, faDollarSign, faCalendarAlt, faGripVertical } from '@fortawesome/free-solid-svg-icons';
import Pagination from './Pagination';

const Applications: React.FC = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [editingApp, setEditingApp] = useState<Application | null>(null);
  const [showPlans, setShowPlans] = useState<string | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [showPlanForm, setShowPlanForm] = useState<boolean>(false);
  const [draggedPlanId, setDraggedPlanId] = useState<string | null>(null);
  const [dragOverPlanId, setDragOverPlanId] = useState<string | null>(null);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [purchaseFormData, setPurchaseFormData] = useState({
    user_id: '',
    user_email: '',
    user_name: ''
  });
  const [analytics, setAnalytics] = useState<{ [key: string]: ApplicationAnalytics }>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const hasFetchedRef = useRef(false);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(10);
  const [planFormData, setPlanFormData] = useState<PlanFormData>({
    name: '',
    description: '',
    pricing_tiers: [],
    selected_features: [],
    consumable_counts: {}
  });
  const [formData, setFormData] = useState<ApplicationFormData>({
    name: '',
    description: '',
    logo_url: '',
    callback_url: '',
    redirect_url: '',
    features: []
  });

  const fetchAnalytics = useCallback(async (applicationId: string) => {
    console.log('📊 fetchAnalytics called for app:', applicationId);
    try {
      const response = await axios.get(`/api/v1/applications/${applicationId}/analytics`);
      console.log('✅ Analytics fetched for app:', applicationId);
      setAnalytics(prev => ({
        ...prev,
        [applicationId]: response.data
      }));
    } catch (err) {
      console.error('❌ Failed to fetch analytics for app:', applicationId, err);
    }
  }, []);

  const fetchApplications = useCallback(async () => {
    console.log('🔄 fetchApplications called - page:', currentPage, 'itemsPerPage:', itemsPerPage);
    try {
      setLoading(true);
      const skip = (currentPage - 1) * itemsPerPage;
      console.log('📡 Making API call to /applications with params:', { skip, limit: itemsPerPage });
      const response = await axios.get('/applications', {
        params: { skip, limit: itemsPerPage }
      });
      const responseData = response.data;
      const apps = responseData.items || responseData; // Handle both old and new format
      
      console.log('✅ Applications fetched:', apps.length, 'apps');
      setApplications(apps);
      
      // Update pagination info from response
      if (responseData.total_count !== undefined) {
        setTotalItems(responseData.total_count);
        setTotalPages(responseData.total_pages);
      }
      
      // Fetch analytics for each application
      console.log('📊 Fetching analytics for', apps.length, 'applications');
      apps.forEach((app: Application) => {
        fetchAnalytics(app.id);
      });
    } catch (err) {
      console.error('❌ Error fetching applications:', err);
      setError(formatErrorMessage(err, 'Failed to fetch applications'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    console.log('🚀 useEffect triggered - fetchApplications will be called');
    if (!hasFetchedRef.current) {
      console.log('🎯 Initial load - calling fetchApplications');
      hasFetchedRef.current = true;
      fetchApplications();
    } else {
      console.log('⏭️ Skipping duplicate call (StrictMode)');
    }
  }, [fetchApplications]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    hasFetchedRef.current = false; // Reset flag when user changes page
  };

  const fetchPlans = useCallback(async (applicationId: string) => {
    try {
      const response = await axios.get(`/plans?application_id=${applicationId}`);
      const responseData = response.data;
      const plansData = responseData.items || responseData; // Handle both old and new format
      setPlans(plansData);
    } catch (err) {
      setError(formatErrorMessage(err, 'Failed to fetch plans'));
    }
  }, []);

  const handleShowPlans = (applicationId: string) => {
    setShowPlans(showPlans === applicationId ? null : applicationId);
    if (showPlans !== applicationId) {
      fetchPlans(applicationId);
    }
  };

  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  }, []);


  const generatePurchaseLink = useCallback((plan: Plan) => {
    setSelectedPlan(plan);
    setShowPurchaseModal(true);
  }, []);

  const handlePurchaseSubmit = async () => {
    if (!selectedPlan || !purchaseFormData.user_id || !purchaseFormData.user_email) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const response = await axios.post('/purchases', {
        user_id: purchaseFormData.user_id,
        user_email: purchaseFormData.user_email,
        user_name: purchaseFormData.user_name || null,
        plan_id: selectedPlan.id
      });

      const purchase = response.data;
      const purchaseUrl = `http://localhost:8001/api/v1/purchase/${purchase.transaction_id}`;
      
      // Copy to clipboard
      await copyToClipboard(purchaseUrl);
      
      // Show success message
      alert(`Purchase link generated and copied to clipboard!\n\nURL: ${purchaseUrl}\n\nTransaction ID: ${purchase.transaction_id}`);
      
      // Reset form and close modal
      setPurchaseFormData({ user_id: '', user_email: '', user_name: '' });
      setShowPurchaseModal(false);
      setSelectedPlan(null);
      
    } catch (err) {
      setError(formatErrorMessage(err, 'Failed to generate purchase link'));
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      // Validate features before submitting
      const validFeatures = formData.features?.filter(f => 
        f.name && f.name.trim() !== '' && 
        f.feature_code && f.feature_code.trim() !== ''
      ) || [];
      
      // Prepare the data to send
      const dataToSend = {
        name: formData.name,
        description: formData.description || undefined,
        logo_url: formData.logo_url || undefined,
        callback_url: formData.callback_url || undefined,
        redirect_url: formData.redirect_url || undefined,
        features: validFeatures.map(f => ({
          name: f.name.trim(),
          feature_code: f.feature_code.trim(),
          description: f.description?.trim() || undefined,
          is_consumable: f.is_consumable || false
        }))
      };
      
      if (editingApp) {
        // Update existing application
        console.log('Updating application:', editingApp.id);
        console.log('Sending data:', {
          ...dataToSend,
          features_count: dataToSend.features.length,
          features: dataToSend.features
        });
        const response = await axios.put(`/applications/${editingApp.id}`, dataToSend);
        console.log('Update response:', response.data);
      } else {
        // Create new application
        console.log('Creating new application');
        console.log('Sending data:', {
          ...dataToSend,
          features_count: dataToSend.features.length,
          features: dataToSend.features
        });
        const response = await axios.post('/applications', {
          ...dataToSend,
          organization_id: user?.organization_id
        });
        console.log('Create response:', response.data);
      }
      
      await fetchApplications();
      resetForm();
    } catch (err: any) {
      console.error('Error saving application:', err);
      console.error('Error response:', err.response?.data);
      setError(formatErrorMessage(err, 'Failed to save application'));
    }
  };

  const handleEdit = (app: Application) => {
    setEditingApp(app);
    setFormData({
      name: app.name,
      description: app.description || '',
      logo_url: app.logo_url || '',
      callback_url: app.callback_url || '',
      redirect_url: app.redirect_url || '',
      features: app.features?.map(f => ({
        name: f.name,
        feature_code: f.feature_code,
        description: f.description || '',
        is_consumable: f.is_consumable
      })) || []
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (appId: string) => {
    if (!window.confirm('Are you sure you want to delete this application?')) {
      return;
    }
    
    try {
      await axios.delete(`/applications/${appId}`);
      await fetchApplications();
    } catch (err) {
      setError(formatErrorMessage(err, 'Failed to delete application'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      logo_url: '',
      callback_url: '',
    redirect_url: '',
      features: []
    });
    setEditingApp(null);
    setShowCreateForm(false);
    setError(null);
  };

  const handleModalClose = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      resetForm();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      resetForm();
    }
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...(prev.features || []), { name: '', feature_code: '', description: '', is_consumable: false }]
    }));
  };

  const removeFeature = (index: number) => {
    const featureToRemove = formData.features?.[index];
    if (featureToRemove && window.confirm(`Are you sure you want to remove the feature "${featureToRemove.name || featureToRemove.feature_code}"?`)) {
      setFormData(prev => ({
        ...prev,
        features: prev.features?.filter((_, i) => i !== index) || []
      }));
    }
  };

  const updateFeature = (index: number, field: keyof FeatureFormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features?.map((feature, i) => 
        i === index ? { ...feature, [field]: value } : feature
      ) || []
    }));
  };

  // Plan management functions
  const handlePlanChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setPlanFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePlanSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editingPlan) {
        await axios.put(`/plans/${editingPlan.id}`, planFormData);
      } else {
        await axios.post(`/plans?application_id=${showPlans}`, planFormData);
      }

      if (showPlans) {
        await fetchPlans(showPlans);
      }
      resetPlanForm();
    } catch (err) {
      setError(formatErrorMessage(err, 'An error occurred'));
    }
  };

  const handlePlanDelete = async (planId: string) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) {
      return;
    }

    try {
      await axios.delete(`/plans/${planId}`);
      if (showPlans) {
        await fetchPlans(showPlans);
      }
    } catch (err) {
      setError(formatErrorMessage(err, 'An error occurred'));
    }
  };

  const handlePlanDragStart = (e: React.DragEvent, planId: string) => {
    setDraggedPlanId(planId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', planId);
  };

  const handlePlanDragOver = (e: React.DragEvent, planId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedPlanId && draggedPlanId !== planId) {
      setDragOverPlanId(planId);
    }
  };

  const handlePlanDragLeave = () => {
    setDragOverPlanId(null);
  };

  const handlePlanDrop = async (e: React.DragEvent, targetPlanId: string) => {
    e.preventDefault();
    setDragOverPlanId(null);
    
    if (!draggedPlanId || draggedPlanId === targetPlanId || !showPlans) {
      setDraggedPlanId(null);
      return;
    }

    const draggedIndex = plans.findIndex(p => p.id === draggedPlanId);
    const targetIndex = plans.findIndex(p => p.id === targetPlanId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedPlanId(null);
      return;
    }

    // Create new ordered array
    const newPlans = [...plans];
    const [draggedPlan] = newPlans.splice(draggedIndex, 1);
    newPlans.splice(targetIndex, 0, draggedPlan);

    // Update local state immediately for better UX
    setPlans(newPlans);

    // Save new order to backend
    try {
      const planIds = newPlans.map(p => p.id);
      await axios.post(`/plans/reorder?application_id=${showPlans}`, planIds, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      // Refresh plans to get updated display_order values
      await fetchPlans(showPlans);
    } catch (err) {
      setError(formatErrorMessage(err, 'Failed to reorder plans'));
      // Revert on error
      await fetchPlans(showPlans);
    }

    setDraggedPlanId(null);
  };

  const handlePlanDragEnd = () => {
    setDraggedPlanId(null);
    setDragOverPlanId(null);
  };

  const handlePlanEdit = (plan: Plan) => {
    setEditingPlan(plan);
    setPlanFormData({
      name: plan.name,
      description: plan.description || '',
      pricing_tiers: plan.pricing_tiers || [],
      selected_features: plan.selected_features || [],
      consumable_counts: plan.consumable_counts || {}
    });
    setShowPlanForm(true);
  };

  const resetPlanForm = () => {
    setPlanFormData({ name: '', description: '', pricing_tiers: [], selected_features: [], consumable_counts: {} });
    setShowPlanForm(false);
    setEditingPlan(null);
    setError(null);
  };

  const updateConsumableCount = (featureCode: string, count: number) => {
    setPlanFormData(prev => ({
      ...prev,
      consumable_counts: {
        ...prev.consumable_counts,
        [featureCode]: count
      }
    }));
  };

  const toggleFeatureSelection = (featureCode: string) => {
    setPlanFormData(prev => {
      const currentFeatures = prev.selected_features || [];
      const isSelected = currentFeatures.includes(featureCode);
      
      let newFeatures;
      if (isSelected) {
        // Remove feature and its consumable count
        newFeatures = currentFeatures.filter(code => code !== featureCode);
        const newConsumableCounts = { ...prev.consumable_counts };
        delete newConsumableCounts[featureCode];
        return {
          ...prev,
          selected_features: newFeatures,
          consumable_counts: newConsumableCounts
        };
      } else {
        // Add feature
        newFeatures = [...currentFeatures, featureCode];
        return {
          ...prev,
          selected_features: newFeatures
        };
      }
    });
  };

  const addPricingTier = () => {
    setPlanFormData(prev => ({
      ...prev,
      pricing_tiers: [...(prev.pricing_tiers || []), { amount: 0, currency: 'USD', duration: 'monthly' }]
    }));
  };

  const removePricingTier = (index: number) => {
    setPlanFormData(prev => ({
      ...prev,
      pricing_tiers: prev.pricing_tiers?.filter((_, i) => i !== index) || []
    }));
  };

  const updatePricingTier = (index: number, field: keyof PricingTier, value: string | number) => {
    setPlanFormData(prev => ({
      ...prev,
      pricing_tiers: prev.pricing_tiers?.map((tier, i) => 
        i === index ? { ...tier, [field]: value } : tier
      ) || []
    }));
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div>Loading applications...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
          Applications
        </h1>
        <p style={{ color: '#6b7280' }}>
          Manage applications for {user?.organization?.name || 'your organization'}
        </p>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          color: '#dc2626',
          padding: '12px',
          borderRadius: '6px',
          marginBottom: '24px'
        }}>
          {error}
        </div>
      )}

      {/* Modal Overlay */}
      {showCreateForm && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={handleModalClose}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
        >
          {/* Modal Content */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '24px 24px 0 24px',
              borderBottom: '1px solid #e5e7eb',
              marginBottom: '24px'
            }}>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: '600', 
                margin: 0, 
                color: '#1f2937',
                marginBottom: '16px'
              }}>
                {editingApp ? 'Edit Application' : 'Add New Application'}
              </h3>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '0 24px 24px 24px' }}>
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                    Application Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Enter application name"
                  />
                </div>
                
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'vertical',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit'
                    }}
                    placeholder="Enter application description"
                  />
                </div>
                
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                    Logo URL
                  </label>
                  <input
                    type="url"
                    name="logo_url"
                    value={formData.logo_url}
                    onChange={handleChange}
                    placeholder="https://example.com/logo.png"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                    Callback URL
                  </label>
                  <input
                    type="url"
                    name="callback_url"
                    value={formData.callback_url}
                    onChange={handleChange}
                    placeholder="https://your-app.com/webhook/purchase"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    URL to receive purchase notifications
                  </p>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                    Redirect URL
                  </label>
                  <input
                    type="url"
                    name="redirect_url"
                    value={formData.redirect_url}
                    onChange={handleChange}
                    placeholder="https://your-app.com/purchase-success"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    URL to redirect users after purchase completion. Will include transaction_id and status as query parameters (optional)
                  </p>
                </div>

                {/* Features Section */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <div>
                      <label style={{ fontWeight: '600', color: '#1f2937', fontSize: '16px' }}>
                        Features
                      </label>
                      <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0 0' }}>
                        Add features that your application provides
                      </p>
                    </div>
                  </div>

                  {formData.features && formData.features.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {formData.features.map((feature, index) => (
                        <div key={index} style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '16px',
                          backgroundColor: '#ffffff',
                          position: 'relative',
                          transition: 'all 0.2s ease'
                        }}>
                          {/* Feature Header */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                fontWeight: '600'
                              }}>
                                {index + 1}
                              </div>
                              <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                                Feature {index + 1}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFeature(index)}
                              style={{ 
                                width: '28px',
                                height: '28px',
                                borderRadius: '6px',
                                backgroundColor: '#fef2f2',
                                border: '1px solid #fecaca',
                                color: '#dc2626',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#fee2e2';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#fef2f2';
                              }}
                              title="Remove feature"
                            >
                              <FontAwesomeIcon icon={faMinus} style={{ fontSize: '10px' }} />
                            </button>
                          </div>

                          {/* Feature Form */}
                          <div style={{ display: 'grid', gap: '12px' }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                                Feature Name *
                              </label>
                              <input
                                type="text"
                                value={feature.name}
                                onChange={(e) => updateFeature(index, 'name', e.target.value)}
                                required
                                style={{
                                  width: '100%',
                                  padding: '10px 12px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '8px',
                                  fontSize: '14px',
                                  boxSizing: 'border-box',
                                  transition: 'border-color 0.2s ease'
                                }}
                                placeholder="e.g., User Authentication, File Upload"
                                onFocus={(e) => {
                                  e.target.style.outline = 'none';
                                  e.target.style.borderColor = '#3b82f6';
                                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#d1d5db';
                                  e.target.style.boxShadow = 'none';
                                }}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                                Feature Code *
                              </label>
                              <input
                                type="text"
                                value={feature.feature_code}
                                onChange={(e) => updateFeature(index, 'feature_code', e.target.value)}
                                required
                                style={{
                                  width: '100%',
                                  padding: '10px 12px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '8px',
                                  fontSize: '14px',
                                  boxSizing: 'border-box',
                                  transition: 'border-color 0.2s ease'
                                }}
                                placeholder="e.g., USER_AUTH, FILE_UPLOAD"
                                onFocus={(e) => {
                                  e.target.style.outline = 'none';
                                  e.target.style.borderColor = '#3b82f6';
                                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#d1d5db';
                                  e.target.style.boxShadow = 'none';
                                }}
                              />
                            </div>

                            <div>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                                Description
                              </label>
                              <textarea
                                value={feature.description}
                                onChange={(e) => updateFeature(index, 'description', e.target.value)}
                                rows={2}
                                style={{
                                  width: '100%',
                                  padding: '10px 12px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '8px',
                                  fontSize: '14px',
                                  resize: 'vertical',
                                  boxSizing: 'border-box',
                                  fontFamily: 'inherit',
                                  transition: 'border-color 0.2s ease'
                                }}
                                placeholder="Describe what this feature does..."
                                onFocus={(e) => {
                                  e.target.style.outline = 'none';
                                  e.target.style.borderColor = '#3b82f6';
                                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#d1d5db';
                                  e.target.style.boxShadow = 'none';
                                }}
                              />
                            </div>

                            {/* Consumable Toggle */}
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '10px',
                              padding: '8px 12px',
                              backgroundColor: '#f8fafc',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0'
                            }}>
                              <div style={{ position: 'relative' }}>
                                <input
                                  type="checkbox"
                                  id={`consumable-${index}`}
                                  checked={feature.is_consumable}
                                  onChange={(e) => updateFeature(index, 'is_consumable', e.target.checked)}
                                  style={{ 
                                    width: '18px', 
                                    height: '18px',
                                    accentColor: '#f59e0b'
                                  }}
                                />
                              </div>
                              <div style={{ flex: 1 }}>
                                <label htmlFor={`consumable-${index}`} style={{ fontSize: '13px', color: '#374151', cursor: 'pointer', fontWeight: '500' }}>
                                  Consumable Feature
                                </label>
                                <p style={{ fontSize: '11px', color: '#6b7280', margin: '2px 0 0 0' }}>
                                  This feature can be consumed/used up (e.g., API calls, storage)
                                </p>
                              </div>
                              {feature.is_consumable && (
                                <div style={{
                                  padding: '2px 6px',
                                  backgroundColor: '#fef3c7',
                                  color: '#92400e',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}>
                                  <FontAwesomeIcon icon={faBolt} style={{ color: '#f59e0b', fontSize: '12px' }} />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {/* Add Feature Button */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        padding: '16px 0',
                        borderTop: '1px solid #e5e7eb',
                        marginTop: '8px'
                      }}>
                        <button
                          type="button"
                          onClick={addFeature}
                          style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            backgroundColor: '#f3f4f6',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            color: '#374151',
                            padding: '10px 16px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#e5e7eb';
                            e.currentTarget.style.borderColor = '#9ca3af';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#f3f4f6';
                            e.currentTarget.style.borderColor = '#d1d5db';
                          }}
                        >
                          <FontAwesomeIcon icon={faPlus} style={{ fontSize: '12px' }} />
                          Add Another Feature
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '32px 20px',
                      border: '2px dashed #d1d5db',
                      borderRadius: '12px',
                      backgroundColor: '#fafafa'
                    }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        backgroundColor: '#f3f4f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 12px auto'
                      }}>
                        <FontAwesomeIcon icon={faPlus} style={{ fontSize: '20px', color: '#9ca3af' }} />
                      </div>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', margin: '0 0 4px 0' }}>
                        No features added yet
                      </h4>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 16px 0' }}>
                        Add features to describe what your application can do
                      </p>
                      <button
                        type="button"
                        onClick={addFeature}
                        style={{ 
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          backgroundColor: '#3b82f6',
                          border: 'none',
                          borderRadius: '8px',
                          color: 'white',
                          padding: '10px 16px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#2563eb';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#3b82f6';
                        }}
                      >
                        <FontAwesomeIcon icon={faPlus} style={{ fontSize: '12px' }} />
                        Add Your First Feature
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Modal Footer */}
                <div style={{ 
                  display: 'flex', 
                  gap: '12px', 
                  justifyContent: 'flex-end',
                  paddingTop: '16px',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <button
                    type="button"
                    onClick={resetForm}
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: '#6b7280',
                      border: '2px solid white',
                      borderRadius: '8px',
                      color: 'white',
                      padding: '0',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      minWidth: '100px',
                      minHeight: '40px'
                    }}
                  >
                    <div style={{
                      padding: '10px 12px',
                      flex: 1,
                      textAlign: 'center'
                    }}>
                      CANCEL
                    </div>
                  </button>
                  <button
                    type="submit"
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: '#16a34a',
                      border: '2px solid white',
                      borderRadius: '8px',
                      color: 'white',
                      padding: '0',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      minWidth: '140px',
                      minHeight: '40px'
                    }}
                  >
                    <div style={{
                      backgroundColor: '#16a34a',
                      padding: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRight: '1px solid rgba(255,255,255,0.3)'
                    }}>
                      <FontAwesomeIcon icon={editingApp ? faSave : faPlus} style={{ fontSize: '14px' }} />
                    </div>
                    <div style={{
                      padding: '10px 12px',
                      flex: 1,
                      textAlign: 'center'
                    }}>
                      {editingApp ? 'UPDATE APPLICATION' : 'ADD APPLICATION'}
                    </div>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Application Button */}
      {!showCreateForm && (
        <div style={{ marginBottom: '24px' }}>
          <button
            onClick={() => setShowCreateForm(true)}
            style={{ 
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#2563eb',
              border: '2px solid white',
              borderRadius: '8px',
              color: 'white',
              padding: '0',
              fontSize: '14px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              cursor: 'pointer',
              overflow: 'hidden',
              minWidth: '180px',
              minHeight: '40px'
            }}
          >
            <div style={{
              backgroundColor: '#2563eb',
              padding: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRight: '1px solid rgba(255,255,255,0.3)'
            }}>
              <FontAwesomeIcon icon={faPlus} style={{ fontSize: '14px' }} />
            </div>
            <div style={{
              padding: '10px 12px',
              flex: 1,
              textAlign: 'center'
            }}>
              ADD NEW APPLICATION
            </div>
          </button>
        </div>
      )}

      {/* Applications List */}
      {applications.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          <FontAwesomeIcon icon={faImage} style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
          <div style={{ fontSize: '18px', marginBottom: '8px' }}>No applications found</div>
          <div>Get started by adding your first application.</div>
        </div>
      ) : (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
              Applications ({applications.length})
            </h3>
          </div>
          <div style={{ padding: '24px' }}>
            <div style={{ display: 'grid', gap: '16px' }}>
              {applications.map((app) => (
                <div key={app.id} style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '20px',
                  backgroundColor: '#fafafa'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        {app.logo_url && (
                          <img 
                            src={app.logo_url} 
                            alt={`${app.name} logo`}
                            style={{ 
                              maxWidth: '80px', 
                              maxHeight: '80px', 
                              borderRadius: '6px',
                              objectFit: 'contain'
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        )}
                        <div>
                          <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                            {app.name}
                          </h4>
                          <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#6b7280' }}>
                            <span>Status: {app.is_active ? 'Active' : 'Inactive'}</span>
                            <span>Created: {new Date(app.created_at).toLocaleDateString()}</span>
                          </div>
                          {analytics[app.id] && (
                            <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <FontAwesomeIcon icon={faChartBar} />
                                {analytics[app.id].total_purchases} total purchases
                              </span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <FontAwesomeIcon icon={faCalendarAlt} />
                                {analytics[app.id].month_purchases} this month
                              </span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <FontAwesomeIcon icon={faDollarSign} />
                                ${analytics[app.id].total_revenue.toFixed(2)} total
                              </span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <FontAwesomeIcon icon={faDollarSign} />
                                ${analytics[app.id].month_revenue.toFixed(2)} this month
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      {app.description && (
                        <p style={{ 
                          color: '#6b7280', 
                          fontSize: '14px', 
                          margin: '8px 0 0 0',
                          lineHeight: '1.5'
                        }}>
                          {app.description}
                        </p>
                      )}
                      {app.features && app.features.length > 0 && (
                        <div style={{ marginTop: '12px' }}>
                          <div style={{ fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                            Features ({app.features.length}):
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {app.features.map((feature, idx) => (
                              <span
                                key={idx}
                                style={{
                                  backgroundColor: feature.is_consumable ? '#fef3c7' : '#e0e7ff',
                                  color: feature.is_consumable ? '#92400e' : '#3730a3',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  border: `1px solid ${feature.is_consumable ? '#f59e0b' : '#6366f1'}`
                                }}
                                title={feature.description || feature.name}
                              >
                                {feature.name}
                                {feature.is_consumable && (
                                  <FontAwesomeIcon icon={faBolt} style={{ color: '#f59e0b', fontSize: '10px', marginLeft: '4px' }} />
                                )}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleShowPlans(app.id)}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: '#8b5cf6',
                          border: '2px solid white',
                          borderRadius: '6px',
                          color: 'white',
                          padding: '0',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          cursor: 'pointer',
                          overflow: 'hidden',
                          minWidth: '80px',
                          minHeight: '32px'
                        }}
                      >
                        <div style={{
                          backgroundColor: '#8b5cf6',
                          padding: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRight: '1px solid rgba(255,255,255,0.3)'
                        }}>
                          <FontAwesomeIcon icon={faList} style={{ fontSize: '10px' }} />
                        </div>
                        <div style={{
                          padding: '6px 8px',
                          flex: 1,
                          textAlign: 'center'
                        }}>
                          PLANS
                        </div>
                      </button>
                      <button
                        onClick={() => handleEdit(app)}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: '#f59e0b',
                          border: '2px solid white',
                          borderRadius: '6px',
                          color: 'white',
                          padding: '0',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          cursor: 'pointer',
                          overflow: 'hidden',
                          minWidth: '80px',
                          minHeight: '32px'
                        }}
                      >
                        <div style={{
                          backgroundColor: '#f59e0b',
                          padding: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRight: '1px solid rgba(255,255,255,0.3)'
                        }}>
                          <FontAwesomeIcon icon={faEdit} style={{ fontSize: '10px' }} />
                        </div>
                        <div style={{
                          padding: '6px 8px',
                          flex: 1,
                          textAlign: 'center'
                        }}>
                          EDIT
                        </div>
                      </button>
                      <button
                        onClick={() => handleDelete(app.id)}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: '#dc2626',
                          border: '2px solid white',
                          borderRadius: '6px',
                          color: 'white',
                          padding: '0',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          cursor: 'pointer',
                          overflow: 'hidden',
                          minWidth: '80px',
                          minHeight: '32px'
                        }}
                      >
                        <div style={{
                          backgroundColor: '#dc2626',
                          padding: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRight: '1px solid rgba(255,255,255,0.3)'
                        }}>
                          <FontAwesomeIcon icon={faTrash} style={{ fontSize: '10px' }} />
                        </div>
                        <div style={{
                          padding: '6px 8px',
                          flex: 1,
                          textAlign: 'center'
                        }}>
                          DELETE
                        </div>
                      </button>
                    </div>
                  </div>
                  
                  {/* Plans Section */}
                  {showPlans === app.id && (
                    <div style={{ 
                      marginTop: '20px', 
                      padding: '20px', 
                      backgroundColor: '#f8fafc', 
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                          Plans ({plans.length})
                        </h4>
                        <button
                          onClick={() => setShowPlanForm(true)}
                          style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#10b981',
                            border: '2px solid white',
                            borderRadius: '6px',
                            color: 'white',
                            padding: '0',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            minWidth: '100px',
                            minHeight: '32px'
                          }}
                        >
                          <div style={{
                            backgroundColor: '#10b981',
                            padding: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRight: '1px solid rgba(255,255,255,0.3)'
                          }}>
                            <FontAwesomeIcon icon={faPlus} style={{ fontSize: '10px' }} />
                          </div>
                          <div style={{
                            padding: '6px 8px',
                            flex: 1,
                            textAlign: 'center'
                          }}>
                            ADD PLAN
                          </div>
                        </button>
                      </div>
                      
                      {plans.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                          <div>No plans found for this application.</div>
                          <div style={{ fontSize: '12px', marginTop: '4px' }}>Create your first plan to get started.</div>
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gap: '12px' }}>
                          {plans.map((plan) => (
                            <div 
                              key={plan.id} 
                              draggable
                              onDragStart={(e) => handlePlanDragStart(e, plan.id)}
                              onDragOver={(e) => handlePlanDragOver(e, plan.id)}
                              onDragLeave={handlePlanDragLeave}
                              onDrop={(e) => handlePlanDrop(e, plan.id)}
                              onDragEnd={handlePlanDragEnd}
                              style={{
                                backgroundColor: 'white',
                                border: draggedPlanId === plan.id 
                                  ? '2px dashed #3b82f6' 
                                  : dragOverPlanId === plan.id 
                                    ? '2px solid #10b981' 
                                    : '1px solid #e5e7eb',
                                borderRadius: '6px',
                                padding: '16px',
                                cursor: draggedPlanId === plan.id ? 'grabbing' : 'grab',
                                opacity: draggedPlanId === plan.id ? 0.5 : 1,
                                transition: 'all 0.2s ease',
                                position: 'relative'
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                                <div 
                                  style={{
                                    cursor: 'grab',
                                    padding: '4px 8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: '#9ca3af',
                                    flexShrink: 0,
                                    marginLeft: '-8px'
                                  }}
                                  title="Drag to reorder"
                                  onMouseDown={(e) => e.stopPropagation()}
                                >
                                  <FontAwesomeIcon icon={faGripVertical} style={{ fontSize: '18px' }} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <h5 style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: '0 0 4px 0' }}>
                                    {plan.name}
                                  </h5>
                                  {plan.description && (
                                    <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 8px 0' }}>
                                      {plan.description}
                                    </p>
                                  )}
                                  <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: '#6b7280' }}>
                                    {plan.pricing_tiers && plan.pricing_tiers.length > 0 && (
                                      <span>
                                        Pricing: {plan.pricing_tiers.map(tier => 
                                          `${tier.currency} ${tier.amount}/${tier.duration}`
                                        ).join(', ')}
                                      </span>
                                    )}
                                    <span>Status: {plan.is_active ? 'Active' : 'Inactive'}</span>
                                  </div>
                                  {plan.consumable_counts && Object.keys(plan.consumable_counts).length > 0 && (
                                    <div style={{ marginTop: '8px' }}>
                                      <div style={{ fontSize: '11px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                                        Consumable Limits:
                                      </div>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                        {Object.entries(plan.consumable_counts).map(([featureCode, count]) => (
                                          <span
                                            key={featureCode}
                                            style={{
                                              backgroundColor: '#fef3c7',
                                              color: '#92400e',
                                              padding: '2px 6px',
                                              borderRadius: '8px',
                                              fontSize: '10px',
                                              fontWeight: '500'
                                            }}
                                          >
                                            {featureCode}: {count}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Selected Features */}
                                  {plan.selected_features && plan.selected_features.length > 0 && (
                                    <div style={{ marginTop: '8px' }}>
                                      <div style={{ fontSize: '11px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                                        Included Features:
                                      </div>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                        {(() => {
                                          const currentApp = applications.find(app => app.id === showPlans);
                                          const allFeatures = currentApp?.features || [];
                                          return plan.selected_features.map((featureCode) => {
                                            const feature = allFeatures.find(f => f.feature_code === featureCode);
                                            return (
                                              <span
                                                key={featureCode}
                                                style={{
                                                  backgroundColor: feature?.is_consumable ? '#dbeafe' : '#f0f9ff',
                                                  color: feature?.is_consumable ? '#1e40af' : '#0c4a6e',
                                                  padding: '2px 6px',
                                                  borderRadius: '8px',
                                                  fontSize: '10px',
                                                  fontWeight: '500',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: '2px'
                                                }}
                                              >
                                                {feature?.name || featureCode}
                                                {feature?.is_consumable && (
                                                  <FontAwesomeIcon icon={faChartBar} style={{ fontSize: '8px' }} />
                                                )}
                                              </span>
                                            );
                                          });
                                        })()}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div style={{ display: 'flex', gap: '6px' }} onMouseDown={(e) => e.stopPropagation()}>
                                  <button
                                    onClick={() => handlePlanEdit(plan)}
                                    style={{ 
                                      display: 'flex',
                                      alignItems: 'center',
                                      backgroundColor: '#f59e0b',
                                      border: '1px solid white',
                                      borderRadius: '4px',
                                      color: 'white',
                                      padding: '4px 8px',
                                      fontSize: '10px',
                                      fontWeight: 'bold',
                                      textTransform: 'uppercase',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faEdit} style={{ fontSize: '8px', marginRight: '4px' }} />
                                    EDIT
                                  </button>
                                  <button
                                    onClick={() => handlePlanDelete(plan.id)}
                                    style={{ 
                                      display: 'flex',
                                      alignItems: 'center',
                                      backgroundColor: '#dc2626',
                                      border: '1px solid white',
                                      borderRadius: '4px',
                                      color: 'white',
                                      padding: '4px 8px',
                                      fontSize: '10px',
                                      fontWeight: 'bold',
                                      textTransform: 'uppercase',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faTrash} style={{ fontSize: '8px', marginRight: '4px' }} />
                                    DELETE
                                  </button>
                                  <button
                                    onClick={() => generatePurchaseLink(plan)}
                                    style={{ 
                                      display: 'flex',
                                      alignItems: 'center',
                                      backgroundColor: '#10b981',
                                      border: '1px solid white',
                                      borderRadius: '4px',
                                      color: 'white',
                                      padding: '4px 8px',
                                      fontSize: '10px',
                                      fontWeight: 'bold',
                                      textTransform: 'uppercase',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faCode} style={{ fontSize: '8px', marginRight: '4px' }} />
                                    LINK
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  
                  {/* App ID Section */}
                  <div style={{ 
                    marginTop: '12px', 
                    padding: '12px', 
                    backgroundColor: '#f8fafc', 
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                      Application ID
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      backgroundColor: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      padding: '8px'
                    }}>
                      <code style={{ 
                        flex: 1, 
                        fontSize: '11px', 
                        fontFamily: 'monospace',
                        color: '#374151',
                        wordBreak: 'break-all'
                      }}>
                        {app.id}
                      </code>
                      <button
                        onClick={() => copyToClipboard(app.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          fontSize: '10px',
                          cursor: 'pointer',
                          flexShrink: 0
                        }}
                        title="Copy Application ID"
                      >
                        <FontAwesomeIcon icon={faCopy} style={{ fontSize: '10px' }} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Plan Form Modal */}
      {showPlanForm && showPlans && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              resetPlanForm();
            }
          }}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              padding: '24px 24px 0 24px',
              borderBottom: '1px solid #e5e7eb',
              marginBottom: '24px'
            }}>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: '600', 
                margin: 0, 
                color: '#1f2937',
                marginBottom: '16px'
              }}>
                {editingPlan ? 'Edit Plan' : 'Add New Plan'}
              </h3>
            </div>

            <div style={{ padding: '0 24px 24px 24px' }}>
              <form onSubmit={handlePlanSubmit}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    Plan Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={planFormData.name}
                    onChange={handlePlanChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                    placeholder="e.g., Basic Plan, Pro Plan"
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={planFormData.description}
                    onChange={handlePlanChange}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      resize: 'vertical'
                    }}
                    placeholder="Describe what this plan includes..."
                  />
                </div>

                {/* Pricing Tiers */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                      Pricing Tiers
                    </label>
                    <button
                      type="button"
                      onClick={addPricingTier}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      + Add Price
                    </button>
                  </div>
                  
                  {planFormData.pricing_tiers && planFormData.pricing_tiers.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {planFormData.pricing_tiers.map((tier, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center',
                          padding: '12px',
                          backgroundColor: '#f8fafc',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={tier.amount}
                            onChange={(e) => updatePricingTier(index, 'amount', parseFloat(e.target.value) || 0)}
                            style={{
                              width: '80px',
                              padding: '6px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '12px',
                              textAlign: 'center'
                            }}
                            placeholder="0.00"
                          />
                          <select
                            value={tier.currency}
                            onChange={(e) => updatePricingTier(index, 'currency', e.target.value)}
                            style={{
                              padding: '6px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '12px',
                              backgroundColor: 'white'
                            }}
                          >
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                            <option value="GBP">GBP</option>
                            <option value="INR">INR</option>
                            <option value="CAD">CAD</option>
                            <option value="AUD">AUD</option>
                          </select>
                          <select
                            value={tier.duration}
                            onChange={(e) => updatePricingTier(index, 'duration', e.target.value)}
                            style={{
                              padding: '6px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '12px',
                              backgroundColor: 'white'
                            }}
                          >
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                            <option value="yearly">Yearly</option>
                            <option value="one-time">One-time</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => removePricingTier(index)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '11px',
                              cursor: 'pointer'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      padding: '20px',
                      textAlign: 'center',
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      color: '#6b7280',
                      fontSize: '12px'
                    }}>
                      No pricing tiers added. Click "Add Price" to add pricing options.
                    </div>
                  )}
                </div>

                {/* Feature Selection */}
                {(() => {
                  const currentApp = applications.find(app => app.id === showPlans);
                  const allFeatures = currentApp?.features || [];
                  return allFeatures.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                        Select Features for this Plan
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {allFeatures.map((feature) => {
                          const isSelected = planFormData.selected_features?.includes(feature.feature_code) || false;
                          return (
                            <div key={feature.feature_code} style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '12px',
                              backgroundColor: isSelected ? '#f0f9ff' : '#f8fafc',
                              borderRadius: '8px',
                              border: `1px solid ${isSelected ? '#0ea5e9' : '#e2e8f0'}`
                            }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleFeatureSelection(feature.feature_code)}
                                style={{
                                  marginRight: '12px',
                                  width: '16px',
                                  height: '16px',
                                  cursor: 'pointer'
                                }}
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '13px', fontWeight: '500', color: '#1f2937' }}>
                                  {feature.name}
                                </div>
                                <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                  Code: {feature.feature_code}
                                  {feature.is_consumable && (
                                    <span style={{ 
                                      marginLeft: '8px', 
                                      backgroundColor: '#fef3c7', 
                                      color: '#92400e', 
                                      padding: '2px 6px', 
                                      borderRadius: '4px',
                                      fontSize: '10px'
                                    }}>
                                      Consumable
                                    </span>
                                  )}
                                </div>
                              </div>
                              {isSelected && feature.is_consumable && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <label style={{ fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                                    Limit:
                                  </label>
                                  <input
                                    type="number"
                                    min="0"
                                    value={planFormData.consumable_counts?.[feature.feature_code] || 0}
                                    onChange={(e) => updateConsumableCount(feature.feature_code, parseInt(e.target.value) || 0)}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                      width: '70px',
                                      padding: '4px 6px',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '4px',
                                      fontSize: '11px',
                                      textAlign: 'center'
                                    }}
                                    placeholder="0"
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={resetPlanForm}
                    style={{ 
                      padding: '10px 20px',
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{ 
                      padding: '10px 20px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    {editingPlan ? 'Update Plan' : 'Create Plan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Link Generation Modal */}
      {showPurchaseModal && selectedPlan && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowPurchaseModal(false);
              setSelectedPlan(null);
              setPurchaseFormData({ user_id: '', user_email: '', user_name: '' });
            }
          }}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', margin: '0 0 24px 0' }}>
              Generate Purchase Link
            </h3>
            
            <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' }}>
                Plan: {selectedPlan.name}
              </h4>
              {selectedPlan.description && (
                <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>
                  {selectedPlan.description}
                </p>
              )}
              {selectedPlan.pricing_tiers && selectedPlan.pricing_tiers.length > 0 && (
                <p style={{ fontSize: '14px', color: '#374151', margin: 0 }}>
                  Price: {selectedPlan.pricing_tiers.map(tier => 
                    `${tier.currency} ${tier.amount}/${tier.duration}`
                  ).join(', ')}
                </p>
              )}
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                User ID <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                name="user_id"
                value={purchaseFormData.user_id || ''}
                onChange={(e) => setPurchaseFormData(prev => ({ ...prev, user_id: e.target.value }))}
                placeholder="External user ID from your system"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                required
              />
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                Unique identifier for the user in your system (required)
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                User Email *
              </label>
              <input
                type="email"
                name="user_email"
                value={purchaseFormData.user_email}
                onChange={(e) => setPurchaseFormData(prev => ({ ...prev, user_email: e.target.value }))}
                placeholder="user@example.com"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                User Name (Optional)
              </label>
              <input
                type="text"
                name="user_name"
                value={purchaseFormData.user_name}
                onChange={(e) => setPurchaseFormData(prev => ({ ...prev, user_name: e.target.value }))}
                placeholder="John Doe"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => {
                  setShowPurchaseModal(false);
                  setSelectedPlan(null);
                  setPurchaseFormData({ user_id: '', user_email: '', user_name: '' });
                }}
                style={{ 
                  padding: '10px 20px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePurchaseSubmit}
                style={{ 
                  padding: '10px 20px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Generate Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {applications.length > 0 && (
        <div className="card">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            showInfo={true}
          />
        </div>
      )}
    </div>
  );
};

export default Applications;
